import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountBalance as AccountIcon,
  ContentCopy as CopyIcon,
  Group as GroupIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import UsersAndGroupsTab from './UsersAndGroupsTab';
import PermissionSetsTab from './PermissionSetsTab';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { ReadOnlyButton } from '../common/ReadOnlyButton';

const AccountDetailView = ({
  account,
  assignments,
  users,
  groups,
  permissionSets,
  onBack,
  onAssignUsersGroups,
  onChangePermissionSets,
  onRemoveAccess,
  onRefresh,
  isRefreshing,
  isLoadingAssignments,
  onNavigateToUser,
  onNavigateToGroup,
  onNavigateToPermissionSet,
  onEditOwners,
  isReadOnly,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPrincipals, setSelectedPrincipals] = useState([]);
  const [selectedPermissionSets, setSelectedPermissionSets] = useState([]);
  const [copied, setCopied] = useState(false);

  const accountId = account.accountId || account.account_id;
  const accountName = account.accountName || account.name || accountId;
  const email = account.email || '';
  const ownerEmails = account.owner_emails || account.ownerEmails || [];

  // Group assignments by principal for Users and Groups tab
  const groupedByPrincipal = React.useMemo(() => {
    const principalMap = new Map();

    assignments.forEach((assignment) => {
      const key = `${assignment.principalType}:${assignment.username || assignment.groupName || assignment.principalName}`;
      if (!principalMap.has(key)) {
        principalMap.set(key, {
          principalType: assignment.principalType,
          principalName: assignment.username || assignment.groupName || assignment.principalName,
          permissionSets: [],
          assignmentIds: [],
        });
      }
      const principal = principalMap.get(key);
      principal.permissionSets.push({
        id: assignment.permissionSetId,
        name: assignment.permissionSetName,
        assignmentId: assignment.id,
        isJIT: assignment.isJIT,
        jitExpiresAt: assignment.jitExpiresAt,
      });
      principal.assignmentIds.push(assignment.id);
    });

    return Array.from(principalMap.values());
  }, [assignments]);

  // Get unique permission sets for Permission Sets tab
  const uniquePermissionSets = React.useMemo(() => {
    const psMap = new Map();

    assignments.forEach((assignment) => {
      if (!psMap.has(assignment.permissionSetId)) {
        // Find the full permission set details
        const fullPs = permissionSets.find(
          (ps) => ps.id === assignment.permissionSetId || ps.permissionSetId === assignment.permissionSetId
        );
        psMap.set(assignment.permissionSetId, {
          id: assignment.permissionSetId,
          name: assignment.permissionSetName,
          description: fullPs?.description || '-',
          arn: fullPs?.arn || fullPs?.permissionSetArn || '-',
          createdAt: fullPs?.createdAt || assignment.createdAt,
          assignmentIds: [],
          hasJIT: false,
          jitCount: 0,
        });
      }
      const psData = psMap.get(assignment.permissionSetId);
      psData.assignmentIds.push(assignment.id);
      if (assignment.isJIT) {
        psData.hasJIT = true;
        psData.jitCount++;
      }
    });

    return Array.from(psMap.values());
  }, [assignments, permissionSets]);

  const handleCopyAccountId = () => {
    navigator.clipboard.writeText(accountId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedPrincipals([]);
    setSelectedPermissionSets([]);
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Back Button */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 16 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          color: '#4698D3',
          fontFamily: 'Metropolis, sans-serif',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#EBF5FB',
          },
        }}
      >
        Back to accounts
      </Button>
      </motion.div>

      {/* Account Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.75rem',
            color: '#253E66',
            mb: 3,
          }}
        >
          {accountName}
        </Typography>

        {/* Overview Card */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#F9FBFD',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
              mb: 2,
            }}
          >
            Overview
          </Typography>

          <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: '#999999',
                  mb: 0.5,
                }}
              >
                Account name
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  color: '#253E66',
                }}
              >
                {accountName}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: '#999999',
                  mb: 0.5,
                }}
              >
                Account ID
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy account ID'}>
                  <IconButton
                    size="small"
                    onClick={handleCopyAccountId}
                    sx={{
                      p: 0.5,
                      color: '#4698D3',
                      '&:hover': {
                        backgroundColor: '#EBF5FB',
                      },
                    }}
                  >
                    <CopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    color: '#253E66',
                  }}
                >
                  {accountId}
                </Typography>
              </Box>
            </Box>

            {email && (
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    color: '#999999',
                    mb: 0.5,
                  }}
                >
                  Email
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    color: '#253E66',
                  }}
                >
                  {email}
                </Typography>
              </Box>
            )}
          </Box>

          {/* JIT Owners Section */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E5E5E5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon sx={{ fontSize: 18, color: '#4698D3' }} />
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: '#253E66',
                  }}
                >
                  JIT Access Owners
                </Typography>
              </Box>
              {onEditOwners && (
                <ReadOnlyButton
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onEditOwners(account)}
                  disabled={isReadOnly}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    color: '#4698D3',
                    '&:hover': {
                      backgroundColor: '#EBF5FB',
                    },
                  }}
                >
                  Edit Owners
                </ReadOnlyButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ownerEmails.length > 0 ? (
                ownerEmails.map((ownerEmail, index) => (
                  <Chip
                    key={index}
                    label={ownerEmail}
                    size="small"
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.8rem',
                      backgroundColor: '#EBF5FB',
                      color: '#253E66',
                      border: '1px solid #4698D3',
                    }}
                  />
                ))
              ) : (
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    color: '#999999',
                    fontStyle: 'italic',
                  }}
                >
                  No owners assigned (SSO admins will approve JIT requests)
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Paper>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ borderBottom: '1px solid #E5E5E5' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              px: 2,
              '& .MuiTab-root': {
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
                fontSize: '0.95rem',
                textTransform: 'none',
                color: '#666666',
                minHeight: 56,
                '&.Mui-selected': {
                  color: '#4698D3',
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4698D3',
                height: 3,
              },
            }}
          >
            <Tab label={`Users and groups (${groupedByPrincipal.length})`} />
            <Tab label={`Permission sets (${uniquePermissionSets.length})`} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {isLoadingAssignments ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                gap: 2,
              }}
            >
              <CircularProgress size={32} sx={{ color: '#4698D3' }} />
              <Typography
                sx={{
                  color: '#666666',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
                Loading assignments...
              </Typography>
            </Box>
          ) : (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              {activeTab === 0 && (
                <UsersAndGroupsTab
                  principals={groupedByPrincipal}
                  selectedPrincipals={selectedPrincipals}
                  onSelectionChange={setSelectedPrincipals}
                  onChangePermissionSets={() => onChangePermissionSets(selectedPrincipals)}
                  onRemoveAccess={() => onRemoveAccess(selectedPrincipals)}
                  onAssignUsersGroups={onAssignUsersGroups}
                  onPrincipalClick={(type, name) => {
                    if (type === 'USER') {
                      onNavigateToUser(name);
                    } else {
                      onNavigateToGroup(name);
                    }
                  }}
                  onRefresh={onRefresh}
                  isRefreshing={isRefreshing}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 1 && (
                <PermissionSetsTab
                  permissionSets={uniquePermissionSets}
                  selectedPermissionSets={selectedPermissionSets}
                  onSelectionChange={setSelectedPermissionSets}
                  onRemove={() => onRemoveAccess(selectedPermissionSets.flatMap(ps => ps.assignmentIds))}
                  onPermissionSetClick={onNavigateToPermissionSet}
                  onRefresh={onRefresh}
                  isRefreshing={isRefreshing}
                  isReadOnly={isReadOnly}
                />
              )}
            </motion.div>
          )}
        </Box>
      </Paper>
      </motion.div>
    </motion.div>
  );
};

export default AccountDetailView;
